import { FC, useState, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { FileUpload, FileUploadHandlerEvent } from 'primereact/fileupload';
import { ProgressSpinner } from 'primereact/progressspinner';

type MigrationMode = null | 'export' | 'import';

interface WaterfallMigrationDialogProps {
  visible: boolean;
  onClose: () => void;
  onExport: () => Promise<void>;
  onImport: (file: File) => Promise<void>;
}

export const WaterfallMigrationDialog: FC<WaterfallMigrationDialogProps> = ({
  visible,
  onClose,
  onExport,
  onImport,
}) => {
  const [mode, setMode] = useState<MigrationMode>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileUploadRef = useRef<FileUpload>(null);

  const handleClose = () => {
    if (isProcessing) return;
    setMode(null);
    setSelectedFile(null);
    setError(null);
    onClose();
  };

  const handleFinish = async () => {
    setError(null);
    setIsProcessing(true);

    try {
      if (mode === 'export') {
        await onExport();
        handleClose();
      } else if (mode === 'import' && selectedFile) {
        await onImport(selectedFile);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileSelect = (e: FileUploadHandlerEvent) => {
    if (e.files && e.files.length > 0) {
      setSelectedFile(e.files[0]);
      setError(null);
    }
  };

  const handleBack = () => {
    setMode(null);
    setSelectedFile(null);
    setError(null);
    fileUploadRef.current?.clear();
  };

  const canFinish = mode === 'export' || (mode === 'import' && selectedFile !== null);

  const footer = (
    <div className="flex justify-end gap-2">
      {mode !== null && (
        <Button
          label="Back"
          icon="pi pi-arrow-left"
          className="p-button-secondary"
          onClick={handleBack}
          disabled={isProcessing}
        />
      )}
      <Button
        label="Cancel"
        className="p-button-secondary"
        onClick={handleClose}
        disabled={isProcessing}
      />
      {mode !== null && (
        <Button
          label={mode === 'export' ? 'Export' : 'Prepare Import'}
          icon={mode === 'export' ? 'pi pi-download' : 'pi pi-upload'}
          className="p-button-primary"
          onClick={handleFinish}
          disabled={!canFinish || isProcessing}
          loading={isProcessing}
        />
      )}
    </div>
  );

  return (
    <Dialog
      header="Waterfall Migration"
      visible={visible}
      onHide={handleClose}
      footer={footer}
      style={{ width: '520px' }}
      dismissableMask={!isProcessing}
      closable={!isProcessing}
    >
      {isProcessing && (
        <div className="flex flex-column align-items-center justify-content-center gap-2 py-4">
          <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" animationDuration=".5s" />
          <span className="text-sm text-gray-600">
            {mode === 'export' ? 'Exporting...' : 'Preparing import...'}
          </span>
        </div>
      )}

      {!isProcessing && mode === null && (
        <div className="flex flex-column gap-3">
          <p className="m-0 text-sm text-gray-600">
            Choose an action to migrate waterfall configurations between environments.
          </p>
          <div className="flex gap-3">
            <Button
              label="Export"
              icon="pi pi-download"
              className="p-button-outlined flex-1"
              style={{ height: '80px' }}
              onClick={() => setMode('export')}
            />
            <Button
              label="Import"
              icon="pi pi-upload"
              className="p-button-outlined flex-1"
              style={{ height: '80px' }}
              onClick={() => setMode('import')}
            />
          </div>
        </div>
      )}

      {!isProcessing && mode === 'export' && (
        <div className="flex flex-column gap-3">
          <div className="bg-blue-50 p-3 border-round">
            <div className="flex align-items-center gap-2">
              <i className="pi pi-info-circle text-blue-600" />
              <span className="text-sm">
                This will export all waterfalls and their versions as a JSON file.
              </span>
            </div>
          </div>
          <p className="m-0 text-sm text-gray-600">
            Click <strong>Export</strong> to download the migration file.
          </p>
        </div>
      )}

      {!isProcessing && mode === 'import' && (
        <div className="flex flex-column gap-3">
          <div className="bg-blue-50 p-3 border-round">
            <div className="flex align-items-center gap-2">
              <i className="pi pi-info-circle text-blue-600" />
              <span className="text-sm">
                Upload a previously exported migration JSON file to compare against the current waterfalls.
              </span>
            </div>
          </div>
          <FileUpload
            ref={fileUploadRef}
            mode="advanced"
            accept=".json"
            maxFileSize={10000000}
            customUpload
            uploadHandler={handleFileSelect}
            auto
            chooseLabel="Choose JSON File"
            className="w-full"
            emptyTemplate={
              <div className="flex align-items-center flex-column py-3">
                <i className="pi pi-file text-4xl text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">Drag and drop a .json file here</span>
              </div>
            }
          />
          {selectedFile && (
            <div className="flex align-items-center gap-2 p-2 surface-100 border-round">
              <i className="pi pi-file text-green-600" />
              <span className="text-sm font-semibold">{selectedFile.name}</span>
              <span className="text-sm text-gray-500">({(selectedFile.size / 1024).toFixed(1)} KB)</span>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="mt-3 p-3 bg-red-50 border-round">
          <div className="flex align-items-center gap-2">
            <i className="pi pi-exclamation-triangle text-red-600" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        </div>
      )}
    </Dialog>
  );
};
